import { useMemo } from 'react';
import { css, type Theme } from '@emotion/react';
import { WarningCircle } from '@phosphor-icons/react';
import { useEditAppContext } from 'client/lib/EditApp';
import { Button, type ButtonProps } from 'client/components/Button';
import { Typography, Flip } from 'client/components/Typography';

interface SaveButtonProps extends ButtonProps {
  error?: string | JSX.Element;
}

export const EditAppSaveButton = ({ error: errorProp, children, ...props }: SaveButtonProps) => {
  const { save, isSaving, validationResult, saveError } = useEditAppContext();
  const showValidation = validationResult.isValidating && !validationResult.isValid;
  const errorSummary = validationResult.errorMessagesByPath['summary']?.[0] || 'Something went wrong while saving. Please try again.';

  const errorMessage = useMemo(() => {
    if (errorProp) {
      return (
        <div css={statusStyle}>
          <div>
            <Typography>{errorProp}</Typography>
          </div>
          <WarningCircle size={18} weight="bold" />
        </div>
      );
    }

    if (showValidation || saveError) {
      return (
        <div css={statusStyle}>
          <div>
            <Typography>{saveError?.details || errorSummary}</Typography>
            {saveError?.status && <Typography variant="fineprint">{saveError.status}</Typography>}
          </div>
          <WarningCircle size={18} weight="bold" />
        </div>
      );
    }

    return <div style={{ flex: 1, height: 42 }} />;
  }, [showValidation, saveError, errorSummary, errorProp]);

  return (
    <div css={containerStyle}>
      <Flip>{errorMessage}</Flip>

      <Button onClick={save} loading={isSaving} {...props}>
        {children}
      </Button>
    </div>
  );
};

const containerStyle = css`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;

  > span {
    flex: 1;
    display: flex;
    position: relative;
    justify-content: flex-end;
    align-items: center;

    > span.container {
      justify-content: flex-end;
    }
  }
`;

const statusStyle = (theme: Theme) => css`
  display: flex;
  align-items: center;
  margin-right: 16px;
  margin-bottom: -4px;
  color: ${theme.color.red[100]};

  > div {
    position: relative;
    // padding-bottom: 4px;
    text-align: right;

    &::after {
      content: '';
      display: block;
      position: absolute;
      right: -17px;
      top: 0;
      width: 4px;
      height: 100%;
      border-radius: 4px;
      background-color: ${theme.color.red[100]};
    }

    > p {
      color: inherit;
    }

    .fineprint {
      color: inherit;
      opacity: 0.8;
    }
  }

  > svg {
    flex-shrink: 0;
    margin-left: 26px;
  }
`;
